import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import NotificationBell from '../components/NotificationBell';
import { 
  Users, 
  Search,
  MapPin,
  Building2,
  Mail,
  Phone,
  ArrowLeft,
  Microscope,
  Edit,
  Trash2,
  MessageSquare
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia",
  "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium",
  "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei",
  "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Canada", "Cape Verde",
  "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica",
  "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic",
  "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Ethiopia",
  "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece",
  "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary",
  "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica",
  "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos",
  "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
  "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania",
  "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco",
  "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua",
  "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau",
  "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
  "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia",
  "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia",
  "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia",
  "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka",
  "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania",
  "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey",
  "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom",
  "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam",
  "Yemen", "Zambia", "Zimbabwe"
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResearcherProfiles = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myProfile, setMyProfile] = useState(null);
  const [connectMessage, setConnectMessage] = useState('');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [connectionStatuses, setConnectionStatuses] = useState({});
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    country: '',
    city: '',
    university: '',
    user_type: '',
    phone: '',
    researchInterests: '',
    profileImage: null,
    google_scholar_url: '',
    orcid_id: ''
  });

  // Fetch researcher profiles from backend
  useEffect(() => {
    // Check if user is authenticated
    if (!user) {
      toast({
        title: t('loginRequired'),
        description: t('loginRequiredMessage'),
        variant: "destructive"
      });
      navigate('/');
      return;
    }

    const fetchProfiles = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/researchers/?user_type=Researcher`);
        if (response.ok) {
          const data = await response.json();
          setProfiles(data);
          
          // Find my profile if logged in
          if (user) {
            const my = data.find(p => p.email === user.email);
            setMyProfile(my);
          }
        }
      } catch (error) {
        console.error('Error fetching profiles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, [user]);

  // Fetch connection statuses when profiles and myProfile are loaded
  useEffect(() => {
    const fetchConnectionStatuses = async () => {
      if (!myProfile || profiles.length === 0) return;
      
      const statuses = {};
      for (const profile of profiles) {
        if (profile.id === myProfile.id) continue;
        
        try {
          const response = await fetch(`${BACKEND_URL}/api/connections/status/${myProfile.id}/${profile.id}`);
          if (response.ok) {
            const data = await response.json();
            statuses[profile.id] = data.status;
          }
        } catch (error) {
          console.error('Error fetching connection status:', error);
        }
      }
      setConnectionStatuses(statuses);
    };

    fetchConnectionStatuses();
  }, [myProfile, profiles]);

  const handleConnect = async (profile) => {
    if (!user || !myProfile) {
      toast({
        title: t('loginRequired'),
        description: t('loginRequiredMessage'),
        variant: "destructive"
      });
      navigate('/');
      return;
    }

    if (myProfile.id === profile.id) {
      toast({
        title: t('cannotConnect'),
        description: t('cannotConnectWithYourself'),
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/connections/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_researcher_id: myProfile.id,
          to_researcher_id: profile.id,
          message: connectMessage
        })
      });

      if (response.ok) {
        toast({
          title: t('connectionRequestSent'),
          description: `${t('requestSentTo')} ${profile.name}`
        });
        setConnectMessage('');
        setSelectedProfile(null);
        
        // Refresh connection statuses
        const statusResponse = await fetch(`${BACKEND_URL}/api/connections/status/${myProfile.id}/${profile.id}`);
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          setConnectionStatuses(prev => ({
            ...prev,
            [profile.id]: statusData.status
          }));
        }
      } else {
        const error = await response.json();
        toast({
          title: t('error'),
          description: error.detail || t('connectionRequestFailed'),
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: t('error'),
        description: t('connectionRequestFailed'),
        variant: "destructive"
      });
    }
  };

  const handleMessageResearcher = (profile) => {
    // Navigate to Dashboard with messages tab and pre-select this researcher
    navigate('/dashboard', { 
      state: { 
        tab: 'messages',
        selectedResearcherId: profile.id,
        selectedResearcherName: profile.name
      } 
    });
  };

  const handleEditProfile = (profile) => {
    setEditForm({
      name: profile.name,
      email: profile.email,
      country: profile.country,
      city: profile.city,
      university: profile.university,
      user_type: profile.user_type || 'Researcher',
      phone: profile.phone || '',
      researchInterests: profile.researchInterests,
      profileImage: profile.profileImage,
      google_scholar_url: profile.google_scholar_url || '',
      orcid_id: profile.orcid_id || ''
    });
    setSelectedProfile(profile);
    setEditDialogOpen(true);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/researchers/${selectedProfile.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast({
          title: t('profileUpdated'),
          description: t('profileUpdatedSuccess')
        });
        setEditDialogOpen(false);
        
        // Refresh profiles
        const refreshResponse = await fetch(`${BACKEND_URL}/api/researchers/`);
        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          setProfiles(data);
          if (user) {
            const my = data.find(p => p.email === user.email);
            setMyProfile(my);
          }
        }
      } else {
        const error = await response.json();
        toast({
          title: t('error'),
          description: error.detail || t('failedToUpdateProfile'),
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failedToUpdateProfile'),
        variant: "destructive"
      });
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm(t('confirmDelete'))) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/researchers/${profileId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({
          title: t('profileDeleted'),
          description: t('profileDeletedSuccess')
        });
        
        // Refresh profiles
        const refreshResponse = await fetch(`${BACKEND_URL}/api/researchers/`);
        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          setProfiles(data);
          setMyProfile(null);
        }
      } else {
        toast({
          title: t('error'),
          description: t('failedToDeleteProfile'),
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failedToDeleteProfile'),
        variant: "destructive"
      });
    }
  };

  const filteredProfiles = profiles.filter(profile => {
    const query = searchQuery.toLowerCase();
    return (
      profile.name.toLowerCase().includes(query) ||
      profile.university.toLowerCase().includes(query) ||
      profile.researchInterests.toLowerCase().includes(query) ||
      profile.city.toLowerCase().includes(query) ||
      profile.country.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_knowledgelink-1/artifacts/8zfj25zj_IMG_6196.png" 
                alt="RCP Logo" 
                className="h-12 w-12 object-contain"
              />
              <span className="text-xl font-bold text-gray-900">RCP</span>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <Button 
                variant="outline" 
                onClick={() => navigate('/')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('backToHome')}
              </Button>
            </div>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="pt-28 pb-20 px-6">
        <div className="container mx-auto">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Users className="h-12 w-12 text-cyan-600" />
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
                {t('researcherProfiles')}
              </h1>
            </div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('researcherProfilesDesc')}
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder={t('searchResearchers')}
                className="pl-12 py-6 text-lg"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="text-center border-cyan-200">
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-cyan-600">{filteredProfiles.length}</CardTitle>
                <CardDescription>{t('activeResearchers')}</CardDescription>
              </CardHeader>
            </Card>
            <Card className="text-center border-cyan-200">
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-cyan-600">25+</CardTitle>
                <CardDescription>{t('countriesRepresented')}</CardDescription>
              </CardHeader>
            </Card>
            <Card className="text-center border-cyan-200">
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-cyan-600">50+</CardTitle>
                <CardDescription>{t('researchFields')}</CardDescription>
              </CardHeader>
            </Card>
          </div>

          {/* Profiles Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
              <p className="mt-4 text-gray-600">{t('loadingProfiles')}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProfiles.map((profile) => (
                <Card key={profile.id} className="overflow-hidden hover:shadow-xl transition-shadow duration-300 border-2 hover:border-cyan-500">
                  <div className="h-48 overflow-hidden bg-gradient-to-br from-cyan-400 to-cyan-600 relative">
                    {profile.profileImage ? (
                      <img 
                        src={profile.profileImage} 
                        alt={profile.name}
                        className="w-32 h-32 rounded-full object-cover border-4 border-white absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2"
                      />
                    ) : (
                      <div className="w-32 h-32 rounded-full bg-gray-300 border-4 border-white absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 flex items-center justify-center">
                        <Users className="h-16 w-16 text-gray-500" />
                      </div>
                    )}
                  </div>
                <CardHeader className="pt-20 text-center">
                  <CardTitle className="text-xl mb-2">{profile.name}</CardTitle>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center justify-center gap-2">
                      <Building2 className="h-4 w-4 text-cyan-600" />
                      <span>{profile.university}</span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="h-4 w-4 text-cyan-600" />
                      <span>{profile.city}, {profile.country}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">{t('researchInterests')}:</h4>
                    <div className="flex flex-wrap gap-2">
                      {profile.researchInterests.split(',').slice(0, 3).map((interest, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-cyan-100 text-cyan-700 hover:bg-cyan-200">
                          {interest.trim()}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="h-4 w-4 text-cyan-600" />
                      <a href={`mailto:${profile.email}`} className="hover:text-cyan-600 transition-colors">
                        {profile.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="h-4 w-4 text-cyan-600" />
                      <span>{profile.phone}</span>
                    </div>
                  </div>

                  {/* Academic Profile Links */}
                  {(profile.google_scholar_url || profile.orcid_id) && (
                    <div className="flex gap-3 pt-2 border-t">
                      {profile.google_scholar_url && (
                        <a 
                          href={profile.google_scholar_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
                          title="Google Scholar Profile"
                        >
                          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 24a7 7 0 1 1 0-14 7 7 0 0 1 0 14zm0-24L0 9.5l4.838 3.94A8 8 0 0 1 12 9a8 8 0 0 1 7.162 4.44L24 9.5z"/>
                          </svg>
                          <span className="text-sm font-medium">Scholar</span>
                        </a>
                      )}
                      {profile.orcid_id && (
                        <a 
                          href={`https://orcid.org/${profile.orcid_id}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors"
                          title="ORCID Profile"
                        >
                          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zM7.369 8.672h1.164v6.656H7.369V8.672zm.582-1.813c0-.371.301-.672.672-.672s.672.301.672.672-.301.672-.672.672-.672-.301-.672-.672zm3.501 1.813h1.116v.907h.016c.156-.295.535-.607 1.102-.607 1.179 0 1.396.776 1.396 1.786v4.113h-1.164v-3.645c0-.434-.009-.993-.605-.993-.606 0-.698.473-.698.962v3.676h-1.164V8.672z"/>
                          </svg>
                          <span className="text-sm font-medium">ORCID</span>
                        </a>
                      )}
                    </div>
                  )}
                  
                  {/* Show Edit/Delete buttons only for own profile */}
                  {myProfile && myProfile.id === profile.id ? (
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-cyan-500 hover:bg-cyan-600"
                        onClick={() => handleEditProfile(profile)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        {t('editProfileBtn')}
                      </Button>
                      <Button 
                        variant="destructive"
                        onClick={() => handleDeleteProfile(profile.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : connectionStatuses[profile.id] === 'accepted' ? (
                    /* Show Message button if connected */
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-green-500 hover:bg-green-600"
                        disabled
                      >
                        ✓ {t('connected')}
                      </Button>
                      <Button 
                        className="flex-1 bg-cyan-500 hover:bg-cyan-600"
                        onClick={() => handleMessageResearcher(profile)}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        {t('message')}
                      </Button>
                    </div>
                  ) : connectionStatuses[profile.id] === 'pending' ? (
                    /* Show Pending status */
                    <Button 
                      className="w-full bg-amber-500 hover:bg-amber-600"
                      disabled
                    >
                      {t('connectionPending')}
                    </Button>
                  ) : (
                    /* Show Connect button */
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          className="w-full bg-cyan-500 hover:bg-cyan-600"
                          onClick={() => setSelectedProfile(profile)}
                        >
                          {t('connect')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t('connectWith')} {profile.name}</DialogTitle>
                          <DialogDescription>
                            {t('sendConnectionRequestMessage')}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="connect-message">{t('messageOptional')}</Label>
                            <Textarea
                              id="connect-message"
                              placeholder={t('connectMessagePlaceholder')}
                              value={connectMessage}
                              onChange={(e) => setConnectMessage(e.target.value)}
                              rows={4}
                            />
                          </div>
                          <Button 
                            onClick={() => handleConnect(profile)}
                            className="w-full bg-cyan-500 hover:bg-cyan-600"
                          >
                            {t('sendConnectionRequest')}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </CardContent>
              </Card>
              ))}
            </div>
          )}

          {!loading && filteredProfiles.length === 0 && (
            <div className="text-center py-12">
              <Microscope className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('noResearchersFound')}</h3>
              <p className="text-gray-600">{t('adjustSearchCriteria')}</p>
            </div>
          )}

          {/* Edit Profile Dialog */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('editYourProfile')}</DialogTitle>
                <DialogDescription>{t('updateProfileInformation')}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                {/* Profile Photo Upload */}
                <div className="space-y-2">
                  <Label htmlFor="edit-profile-photo">{t('profilePhoto')}</Label>
                  <Input 
                    id="edit-profile-photo" 
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          toast({
                            title: t('error'),
                            description: t('imageSizeTooLarge'),
                            variant: "destructive"
                          });
                          e.target.value = null;
                          return;
                        }
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setEditForm({...editForm, profileImage: reader.result});
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  {editForm.profileImage && (
                    <img 
                      src={editForm.profileImage} 
                      alt="Preview" 
                      className="w-24 h-24 rounded-full object-cover border-2 border-cyan-500"
                    />
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-name">{t('fullNameRequired')}</Label>
                    <Input 
                      id="edit-name" 
                      value={editForm.name}
                      onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                      required 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-email">{t('emailRequired')}</Label>
                    <Input 
                      id="edit-email" 
                      type="email" 
                      value={editForm.email}
                      onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                      required 
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-country">{t('countryRequired')}</Label>
                    <Select
                      value={editForm.country}
                      onValueChange={(value) => setEditForm({...editForm, country: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('selectCountry')} />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country} value={country}>
                            {country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-city">{t('cityRequired')}</Label>
                    <Input 
                      id="edit-city" 
                      value={editForm.city}
                      onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                      required 
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-university">{t('universityRequired')}</Label>
                  <Input 
                    id="edit-university" 
                    value={editForm.university}
                    onChange={(e) => setEditForm({...editForm, university: e.target.value})}
                    required 
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-user-type">{t('iAm')} *</Label>
                  <Select
                    value={editForm.user_type}
                    onValueChange={(value) => setEditForm({...editForm, user_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('selectYourRole')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Researcher">{t('researcher')}</SelectItem>
                      <SelectItem value="Research Assistant">{t('researchAssistant')}</SelectItem>
                      <SelectItem value="Both">{t('both')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-phone">{t('phoneOptional')}</Label>
                  <Input 
                    id="edit-phone" 
                    type="tel" 
                    value={editForm.phone}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-interests">{t('researchInterestsRequired')}</Label>
                  <Textarea 
                    id="edit-interests" 
                    value={editForm.researchInterests}
                    onChange={(e) => setEditForm({...editForm, researchInterests: e.target.value})}
                    rows={5}
                    required 
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-google-scholar">{t('googleScholarOptional')}</Label>
                  <Input 
                    id="edit-google-scholar" 
                    type="url"
                    value={editForm.google_scholar_url}
                    onChange={(e) => setEditForm({...editForm, google_scholar_url: e.target.value})}
                    placeholder={t('googleScholarPlaceholder')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-orcid">{t('orcidOptional')}</Label>
                  <Input 
                    id="edit-orcid" 
                    type="text"
                    value={editForm.orcid_id}
                    onChange={(e) => setEditForm({...editForm, orcid_id: e.target.value})}
                    placeholder={t('orcidPlaceholder')}
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 bg-cyan-500 hover:bg-cyan-600">
                    {t('updateProfile')}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                    {t('cancel')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* CTA Section */}
          <div className="mt-16 text-center bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-3xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-4">{t('joinResearchCommunity')}</h2>
            <p className="text-lg mb-6 text-cyan-50">
              {t('createProfileCollaborate')}
            </p>
            <Button 
              size="lg" 
              className="bg-white text-cyan-600 hover:bg-gray-100"
              onClick={() => navigate('/', { state: { scrollTo: 'profiles' } })}
            >
              {t('createYourProfile')}
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_knowledgelink-1/artifacts/8zfj25zj_IMG_6196.png" 
              alt="RCP Logo" 
              className="h-10 w-10 object-contain"
            />
            <span className="text-xl font-bold">RCP</span>
          </div>
          <p className="text-gray-400">
            {t('footerTagline')}
          </p>
          <div className="border-t border-gray-800 mt-8 pt-8">
            <p className="text-gray-400">{t('copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ResearcherProfiles;
